import { Button } from '@civilgrid/ui/components';
import { useMutation } from '@tanstack/react-query';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { triggerDigestForProject } from '~/api/internal';
import ControlledCheckbox from '~/components2/forms/controlled/ControlledCheckbox';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import { MajorHeading } from '../common/Heading';

type TFormValues = { projectId: string; shouldSendToProjectMembers: boolean };

export default function NotificationTools() {
  const form = useForm<TFormValues>({
    defaultValues: { projectId: '', shouldSendToProjectMembers: false },
  });
  const projectId = form.watch('projectId');

  const mutation = useMutation({
    mutationKey: ['trigger-digest-for-project', { projectId }],
    mutationFn: (props: { id: string; shouldSendToProjectMembers: string }) =>
      triggerDigestForProject(props.id, props.shouldSendToProjectMembers),
    onSuccess: () => {
      form.reset({});
      Notify.info({
        message: `Project Weekly Digest started for ${projectId}`,
      });
    },
    onError: () => {
      Notify.error({
        message: 'An error occurred while starting the project weekly digest!',
      });
    },
  });

  const onSubmit: SubmitHandler<TFormValues> = (data) => {
    mutation.mutate({
      id: data.projectId,
      shouldSendToProjectMembers: data.shouldSendToProjectMembers ? 'YES' : '',
    });
  };

  const isProcessing = mutation.isPending;
  const isDisabled = !form.formState.isValid || isProcessing;

  return (
    <div className="flex flex-col items-start space-y-4">
      <div>
        <MajorHeading tooltip="Enter a 24-digit Project ID to send the project's weekly digest to your email. NOTE: This will only send the digest if request status updates have been made to the project within the last week.">
          Send Digest for Project
        </MajorHeading>
        <Form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col items-start space-y-4">
          <ControlledInput
            control={form.control}
            name="projectId"
            label="24-Digit Project ID"
            isRequired
            placeholder="Ex: 66fc5eb3c2d480c70976e850"
          />

          <ControlledCheckbox
            control={form.control}
            name="shouldSendToProjectMembers"
            label="Send to all project members? (INCLUDING NON-ADMINS)"
          />

          <Button type="submit" loading={isProcessing} disabled={isDisabled}>
            Send Digest
          </Button>
        </Form>
      </div>
    </div>
  );
}
